import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { WelcomeStep } from './steps/WelcomeStep';
import { FeatureStep } from './steps/FeatureStep';
import { ActionStep } from './steps/ActionStep';
import { CompletionStep } from './steps/CompletionStep';
import { UserProfileStep } from './steps/UserProfileStep';
import { AppDiscoveryStep } from './steps/AppDiscoveryStep';
import { ConnectionTutorialStep } from './steps/ConnectionTutorialStep';
import { ConfigGenerationStep } from './steps/ConfigGenerationStep';

export function OnboardingModal() {
  const {
    isOnboarding,
    currentFlow,
    currentStep,
    progress,
    nextStep,
    previousStep,
    skipStep,
    endOnboarding,
  } = useOnboarding();

  if (!isOnboarding || !currentFlow || !currentStep || !progress) {
    return null;
  }

  const isFirstStep = progress.currentStepIndex === 0;
  const isLastStep = progress.currentStepIndex === currentFlow.steps.length - 1;
  const progressPercentage = ((progress.currentStepIndex + 1) / currentFlow.steps.length) * 100;

  // Render appropriate step component
  const renderStepContent = () => {
    if (currentStep.component) {
      const StepComponent = currentStep.component;
      return <StepComponent />;
    }

    // Default step rendering based on step ID
    if (currentStep.id.includes('welcome')) {
      return <WelcomeStep step={currentStep} flow={currentFlow} />;
    } else if (currentStep.id.includes('completion') || currentStep.id.includes('complete')) {
      return <CompletionStep step={currentStep} flow={currentFlow} />;
    } else if (currentStep.id.includes('profile')) {
      return <UserProfileStep step={currentStep} />;
    } else if (currentStep.id.includes('app_discovery') || currentStep.id.includes('business_app_discovery')) {
      return <AppDiscoveryStep step={currentStep} />;
    } else if (currentStep.id.includes('connection_tutorial')) {
      return <ConnectionTutorialStep step={currentStep} />;
    } else if (currentStep.id.includes('config_generation')) {
      return <ConfigGenerationStep step={currentStep} />;
    } else if (currentStep.requiredAction) {
      return <ActionStep step={currentStep} />;
    } else {
      return <FeatureStep step={currentStep} />;
    }
  };

  return (
    <Transition.Root show={isOnboarding} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          if (currentStep.skipable !== false) {
            endOnboarding(false);
          }
        }}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                {/* Close button */}
                {currentStep.skipable !== false && (
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => endOnboarding(false)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                )}

                {/* Progress bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {currentFlow.name}
                    </h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Step {progress.currentStepIndex + 1} of {currentFlow.steps.length}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Step content */}
                <div className="mt-3">
                  {renderStepContent()}
                </div>

                {/* Navigation buttons */}
                <div className="mt-6 flex items-center justify-between">
                  <div>
                    {!isFirstStep && (
                      <button
                        type="button"
                        className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        onClick={previousStep}
                      >
                        Back
                      </button>
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    {currentStep.skipable && !isLastStep && (
                      <button
                        type="button"
                        className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        onClick={skipStep}
                      >
                        Skip
                      </button>
                    )}
                    
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      onClick={() => {
                        if (isLastStep) {
                          endOnboarding(true);
                        } else {
                          nextStep();
                        }
                      }}
                    >
                      {isLastStep ? 'Get Started' : 'Next'}
                    </button>
                  </div>
                </div>

                {/* Estimated time */}
                {currentFlow.estimatedTime && progress.currentStepIndex === 0 && (
                  <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Estimated time: {currentFlow.estimatedTime} minutes
                    </p>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}